import type {
  ContextualSegmentsDrawerState,
  ContextualSegmentsV2FormState,
} from '@openx/types/targeting/advancedTargeting.ts';
import type { CustomVariablesDataPayload } from '@openx/types/targeting/customVariables.ts';
import { ComparisonType } from '@openx/types/targeting/targetingValuesTypes.ts';
import isEqual from 'lodash/isEqual';
import {
  normalizeProviderId,
  parseCustomFormatToProviderSelections,
  providerSelectionsToCustomFormat,
} from '../ContextualSegments/utils/enrichment.ts';

const emptyContextualSegmentsV2: ContextualSegmentsV2FormState = {
  attr: '',
  excludes: [],
  includes: [],
  op: ComparisonType.INTERSECTS,
};

export const emptyContextualSegmentsDrawerState: ContextualSegmentsDrawerState = {
  contextual: [],
  contextualV2: emptyContextualSegmentsV2,
};

/** V2 drawer state (a single provider) to the contextual segments form field. */
export const contextualV2ToFormField = ({ attr, includes, excludes }: ContextualSegmentsV2FormState) =>
  providerSelectionsToCustomFormat([{ providerId: attr, includes, excludes }]);

/**
 * Contextual segments form field to the V2 drawer state. V2 edits one provider: the account's when the field has
 * its segments, otherwise the first one (the account's provider changed, which V2 warns about and saves under the new
 * provider). Segments of other providers are kept on save by `getContextualSegmentsFromDrawerState`.
 */
export const contextualToV2FormState = (
  contextual: CustomVariablesDataPayload[],
  accountProviderId?: string
): ContextualSegmentsV2FormState => {
  const selections = parseCustomFormatToProviderSelections(contextual);
  const accountProvider = accountProviderId ? normalizeProviderId(accountProviderId) : undefined;
  const selection = selections.find(({ providerId }) => providerId === accountProvider) ?? selections[0];

  return {
    ...emptyContextualSegmentsV2,
    attr: selection?.providerId ?? '',
    includes: selection?.includes ?? [],
    excludes: selection?.excludes ?? [],
  };
};

export const getContextualSegmentsDrawerState = (
  contextual: CustomVariablesDataPayload[] | null | undefined,
  segmentsV2 = false,
  accountProviderId?: string
): ContextualSegmentsDrawerState => ({
  contextual: contextual ?? [],
  contextualV2: segmentsV2 ? contextualToV2FormState(contextual ?? [], accountProviderId) : emptyContextualSegmentsV2,
});

export const getContextualSegmentsFromDrawerState = (
  state: ContextualSegmentsDrawerState,
  segmentsV2 = false,
  accountProviderId?: string
): CustomVariablesDataPayload[] => {
  if (!segmentsV2) return state.contextual;

  // In V2 `state.contextual` keeps the loaded value; segments of providers the drawer did not edit are kept as they are
  const editedProviders = new Set([
    contextualToV2FormState(state.contextual, accountProviderId).attr,
    state.contextualV2.attr,
  ]);
  const otherProviders = state.contextual.filter(({ attr }) => !editedProviders.has(normalizeProviderId(attr)));

  return [...contextualV2ToFormField(state.contextualV2), ...otherProviders];
};

export const isContextualUpdatedCheck = (
  initState: ContextualSegmentsDrawerState,
  params: ContextualSegmentsDrawerState,
  segmentsV2 = false
): boolean => {
  if (segmentsV2) {
    const { attr, includes, excludes } = params.contextualV2;
    // The provider is auto-detected from the account, so a changed provider must be savable on its own.
    const isProviderChanged = attr !== initState.contextualV2.attr && Boolean(includes.length || excludes.length);

    return Boolean(
      isProviderChanged ||
        params.contextualV2.includes.filter(x => !initState.contextualV2.includes.includes(x)).length ||
        initState.contextualV2.includes.filter(x => !params.contextualV2.includes.includes(x)).length ||
        params.contextualV2.excludes.filter(x => !initState.contextualV2.excludes.includes(x)).length ||
        initState.contextualV2.excludes.filter(x => !params.contextualV2.excludes.includes(x)).length
    );
  }

  return !hasIncompleteContextualInputs(params, segmentsV2) && !isEqual(initState.contextual, params.contextual);
};

/** V1 drawer state with a provider row whose segment ids are still empty, which cannot be saved yet. */
export const hasIncompleteContextualInputs = (params: ContextualSegmentsDrawerState, segmentsV2 = false): boolean =>
  !segmentsV2 && params.contextual.some(contextualSegment => !contextualSegment.val);
